import {AfterViewInit, Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';
import {HttpErrorResponse} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {
  AccessAreaId,
  CentralAuthService,
  CentralSession
} from '../../services/central-auth.service';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(config: {client_id: string; callback: (response: {credential: string}) => void}): void;
          renderButton(element: HTMLElement, options: Record<string, unknown>): void;
        };
      };
    };
  }
}

type PortalIcon = 'globe' | 'person' | 'truck' | 'chart' | 'monitor' | 'brain' | 'trend';
type PortalTheme = 'kenya' | 'south-africa' | 'insights';

interface ServiceLink {
  title: string;
  description: string;
  icon: PortalIcon;
  match: string[];
  exclude?: string[];
  organisationIds?: number[];
  countryAccess?: 'Kenya' | 'South Africa';
  fulfillmentService?: boolean;
  excludedRoles?: string[];
  roles: string[];
  url?: string;
  directUrl?: string;
}

interface AccessArea {
  id: string;
  title: string;
  description: string;
  theme: PortalTheme;
  icon: PortalIcon;
  services: ServiceLink[];
}

@Component({
  selector: 'app-access-portal',
  templateUrl: './access-portal.component.html',
  styleUrls: ['./access-portal.component.scss']
})
export class AccessPortalComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly loginForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(6)]]
  });
  readonly accessAreas: AccessArea[] = [
    {
      id: 'kenya',
      title: 'Access Kenya',
      description: 'Access HIV PDS operational services for Kenya.',
      theme: 'kenya',
      icon: 'globe',
      services: [
        {
          title: 'Client Enrollment Form', description: 'Register and enroll clients', icon: 'person',
          match: [
            'hiv pds client enrollment form kenya', 'hiv pds client enrollment',
            'hiv pds client enrolment form kenya', 'hiv pds client enrolment'
          ],
          exclude: ['south africa'],
          organisationIds: [60],
          excludedRoles: ['sub_county'],
          roles: [
            'health_worker', 'coordinator', 'boh', 'isinya', 'kitengela',
            'county', 'sub_county', 'hiv_pds'
          ],
          url: environment.serviceUrls.kenyaEnrollment
        },
        {
          title: 'Delivery Form', description: 'Create and manage deliveries', icon: 'truck',
          match: ['hiv pds delivery form kenya', 'hiv pds delivery form'],
          exclude: ['south africa'],
          organisationIds: [63],
          fulfillmentService: true,
          roles: ['fulfillment', 'hiv_pds'],
          url: environment.serviceUrls.kenyaDelivery
        },
        {
          title: 'Delivery Dashboard',
          description: 'View project delivery performance across all assigned Kenya facilities',
          icon: 'chart',
          match: [
            'hiv pds dashboard kenya', 'ke dashboard', 'kenya dashboard',
            'ke delivery dashboard', 'kenya delivery dashboard', 'delivery dashboard'
          ],
          exclude: ['south africa', 'za dashboard'],
          organisationIds: [56],
          countryAccess: 'Kenya',
          roles: [
            'health_worker', 'coordinator', 'boh', 'isinya', 'kitengela',
            'partner', 'sub_county', 'county',
            'business_user', 'cxo_business_user', 'business_partners',
            'hiv_pds'
          ]
        }
      ]
    },
    {
      id: 'south-africa',
      title: 'Access South Africa',
      description: 'Access HIV PDS operational services for South Africa.',
      theme: 'south-africa',
      icon: 'globe',
      services: [
        {
          title: 'Client Enrollment Form', description: 'Register and enroll clients', icon: 'person',
          match: ['hiv pds client enrollment form south africa', 'hiv pds client enrolment form south africa'],
          organisationIds: [87],
          countryAccess: 'South Africa',
          excludedRoles: ['sub_county'],
          roles: ['sa_health_worker', 'health_worker', 'coordinator', 'county', 'sub_county']
        },
        {
          title: 'Delivery Form', description: 'Create and manage deliveries', icon: 'truck',
          match: ['hiv pds delivery form south africa'],
          organisationIds: [88],
          fulfillmentService: true,
          roles: ['sa_fulfillment', 'fulfillment']
        },
        {
          title: 'Delivery Dashboard',
          description: 'View South Africa project performance across all assigned facilities',
          icon: 'chart',
          match: ['hiv pds dashboard south africa', 'za dashboard'],
          organisationIds: [86],
          countryAccess: 'South Africa',
          roles: [
            'sa_health_worker', 'health_worker', 'coordinator',
            'partner', 'county', 'sub_county', 'business_user',
            'cxo_business_user', 'business_partners'
          ]
        }
      ]
    },
    {
      id: 'insights',
      title: 'HIV PDS Insights',
      description: 'Cross-country insights and advanced analytics.',
      theme: 'insights',
      icon: 'trend',
      services: [
        {
          title: 'Agentic AI Insights',
          description: 'AI-powered insights across Kenya & South Africa',
          icon: 'brain',
          match: ['agentic ai insights', 'hiv pds insights', 'ai insights'],
          organisationIds: [112],
          roles: ['business_partners', 'cxo_business_user'],
          directUrl: environment.accessPortalUrls.insights
        },
        {
          title: 'Coordinator',
          description: 'Access the coordinator dashboard and assigned operational features',
          icon: 'monitor',
          match: ['coordinator dashboard', 'coordinator'],
          roles: ['business_partners']
        },
        {
          title: 'Predictive Analytics – Patient Adherence',
          description: 'Identify at-risk patients and improve adherence outcomes',
          icon: 'trend',
          match: ['predictive analytics', 'predictive dashboard', 'patient adherence', 'isinya'],
          organisationIds: [70, 100],
          roles: [
            'boh', 'isinya', 'kitengela', 'business_partners', 'coordinator',
            'county', 'sub_county', 'cxo_business_user'
          ]
        }
      ]
    }
  ];
  session: CentralSession | null = null;
  loading = true;
  submitting = false;
  emailModalOpen = false;
  passwordVisible = false;
  errorMessage = '';
  openingService = '';
  private watchdog?: ReturnType<typeof setInterval>;

  constructor(
    private fb: FormBuilder,
    private auth: CentralAuthService,
    private zone: NgZone
  ) {}

  get visibleAccessAreas(): AccessArea[] {
    const granted = new Set<AccessAreaId>(this.session?.accessAreas || []);
    return this.accessAreas.filter(area =>
      granted.has(area.id as AccessAreaId) && this.visibleServices(area).length > 0
    );
  }

  async ngOnInit(): Promise<void> {
    this.session = await this.auth.restoreSession();
    if (this.session) await this.loadEntitlements();
    this.loading = false;
    if (!this.session) setTimeout(() => this.renderGoogleButton());
    this.watchdog = setInterval(() => this.validateSession(), environment.checkTokenInterval);
  }

  ngAfterViewInit(): void {
    this.loadGoogleIdentity();
  }

  ngOnDestroy(): void {
    if (this.watchdog) clearInterval(this.watchdog);
  }

  async signIn(): Promise<void> {
    this.errorMessage = '';
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      this.errorMessage = 'Enter a valid email and a password of at least 6 characters.';
      return;
    }

    this.submitting = true;
    this.loading = true;
    try {
      const credentials = this.loginForm.getRawValue();
      this.session = await this.auth.emailLogin(credentials.email.trim(), credentials.password);
      await this.loadEntitlements();
      this.emailModalOpen = false;
      this.loginForm.reset();
    } catch (error) {
      this.errorMessage = this.describeError(error, 'We could not sign you in. Check your credentials and try again.');
    } finally {
      this.submitting = false;
      this.loading = false;
    }
  }

  logout(): void {
    this.auth.logout();
    this.session = null;
    this.errorMessage = '';
    setTimeout(() => this.renderGoogleButton());
  }

  serviceAvailable(service: ServiceLink): boolean {
    const organisation = this.auth.findOrganisation(
      service.match, service.exclude, service.organisationIds, service.countryAccess
    );
    return !!organisation;
  }

  visibleServices(area: AccessArea): ServiceLink[] {
    const roles = this.session?.roles || [];
    const administrator = roles.some(role =>
      ['super_admin', 'admin_eng', 'administrator'].includes(role)
    );
    const fulfillmentUser = roles.some(role =>
      ['fulfillment', 'sa_fulfillment'].includes(role)
    );
    return area.services.filter(service =>
      (administrator || !fulfillmentUser || service.fulfillmentService) &&
      (administrator || !service.excludedRoles?.some(role => roles.includes(role))) &&
      (administrator || service.roles.some(role => roles.includes(role))) &&
      this.serviceAvailable(service)
    );
  }

  async accessService(service: ServiceLink): Promise<void> {
    const organisation = this.auth.findOrganisation(
      service.match, service.exclude, service.organisationIds, service.countryAccess
    );
    if (!organisation) {
      this.errorMessage = `${service.title} is not configured for your account. Contact your administrator.`;
      return;
    }
    this.openingService = service.title;
    this.errorMessage = '';
    try {
      if (service.directUrl) {
        this.auth.openDirectUrl(service.directUrl);
      } else {
        await this.auth.openOrganisation(organisation, service.url);
      }
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
        this.auth.logout();
        this.session = null;
        this.errorMessage = 'Your session has expired. Please sign in again.';
        setTimeout(() => this.renderGoogleButton());
      } else {
        this.errorMessage = this.describeError(error, `Unable to open ${service.title}.`);
      }
    } finally {
      this.openingService = '';
    }
  }

  openEmailLogin(): void {
    this.errorMessage = '';
    this.emailModalOpen = true;
    setTimeout(() => document.getElementById('email')?.focus());
  }

  closeEmailLogin(): void {
    if (this.submitting) return;
    this.emailModalOpen = false;
    this.errorMessage = '';
    this.loginForm.reset();
  }

  trackArea(_: number, area: AccessArea): string {
    return area.id;
  }

  trackService(_: number, service: ServiceLink): string {
    return service.title;
  }

  private async loadEntitlements(): Promise<void> {
    try {
      this.session = await this.auth.refreshEntitlements();
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
        this.auth.logout();
        this.session = null;
        this.errorMessage = 'Your session has expired. Please sign in again.';
        return;
      }
      // Login response entitlements remain usable when the organisation refresh is unavailable.
      this.errorMessage = 'Your access was loaded from your profile. Some recent access changes may not appear yet.';
    }
  }

  private async validateSession(): Promise<void> {
    if (!this.session) return;
    const restored = await this.auth.restoreSession();
    this.zone.run(() => {
      if (restored) {
        this.session = restored;
      } else {
        this.session = null;
        this.errorMessage = 'Your session has expired. Please sign in again.';
        setTimeout(() => this.renderGoogleButton());
      }
    });
  }

  private loadGoogleIdentity(): void {
    if (window.google) {
      this.renderGoogleButton();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>('script[data-google-identity]');
    if (existing) {
      existing.addEventListener('load', () => this.renderGoogleButton());
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.dataset['googleIdentity'] = 'true';
    script.addEventListener('load', () => this.renderGoogleButton());
    document.head.appendChild(script);
  }

  private renderGoogleButton(): void {
    const element = document.getElementById('google-signin');
    if (!element || !window.google || this.session) return;
    element.innerHTML = '';
    window.google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: response => this.zone.run(async () => {
        this.submitting = true;
        this.loading = true;
        this.errorMessage = '';
        try {
          this.session = await this.auth.googleLogin(response.credential);
          await this.loadEntitlements();
        } catch (error) {
          this.errorMessage = this.describeError(error, 'Google sign-in failed. Please try again.');
        } finally {
          this.submitting = false;
          this.loading = false;
        }
      })
    });
    window.google.accounts.id.renderButton(element, {
      type: 'standard',
      shape: 'pill',
      theme: 'outline',
      text: 'signin_with',
      size: 'medium',
      logo_alignment: 'left',
      width: 280
    });
  }

  private describeError(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      return error.error?.message || (error.status === 0
        ? 'The authentication service is currently unreachable.'
        : fallback);
    }
    return error instanceof Error ? error.message : fallback;
  }
}
